/**
 * Cloud Upload API Services
 *
 * React Query hooks for cloud-to-dataset upload operations.
 * Uses the Phase 5 batch upload API for reliable cloud imports.
 *
 * Backend API:
 * - POST /datasets/{id}/upload-from-cloud - Start batch upload
 * - GET /datasets/{id}/upload-from-cloud - List active batches
 * - GET /datasets/{id}/upload-from-cloud/{batch_id} - Poll batch status
 */

import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { nuclearn_api_url } from "src/App";
import type { BatchStatusEvent } from "src/types/notificationEvents";
import {
  useNotificationStream,
  useNotificationStreams,
  isNotificationStreamHealthy,
  useSSEFallbackInvalidation,
} from "src/reusable/hooks/useNotificationStream";
import {
  isRateLimitError,
  getRetryAfterDelay,
} from "src/reusable/services/unifiedChunkedUploadService";
import { assertValidCloudPath } from "src/reusable/utils/cloudPathValidation";
import type {
  CloudProviderType,
  CloudPathItem,
  CloudBatchUploadRequest,
  CloudBatchUploadResponse,
  CloudBatchStatusResponse,
  CloudBatchListResponse,
  CloudBatchStatus,
  CloudBatchSummary,
} from "src/types/cloudFiles";

// =============================================================================
// Constants
// =============================================================================

/** Maximum number of rate limit retries before giving up */
const MAX_RATE_LIMIT_RETRIES = 3;
/** Polling interval (ms) for active batch status */
export const BATCH_POLL_INTERVAL_MS = 2000;
const TERMINAL_STATUSES: CloudBatchStatus[] = [
  "completed",
  "completed_with_errors",
  "failed",
  "cancelled",
];

// =============================================================================
// Batch Upload API
// =============================================================================

/**
 * Start a batch upload from cloud storage.
 * POST /datasets/{id}/upload-from-cloud
 */
const startBatchUpload = async (
  datasetId: number,
  request: CloudBatchUploadRequest,
): Promise<CloudBatchUploadResponse> => {
  // Validate all paths before making API call (defense-in-depth)
  for (const item of request.paths) {
    assertValidCloudPath(item.path);
  }

  const response = await axios.post(
    `${nuclearn_api_url}/datasets/${datasetId}/upload-from-cloud`,
    request,
  );
  return response.data;
};

/**
 * List cloud upload batches for a dataset, including recently completed
 * ones so the UI can render both "Active Cloud Imports" and "Recent
 * Imports" sections from a single response.
 * GET /datasets/{id}/upload-from-cloud
 */
export const listActiveBatches = async (
  datasetId: number,
): Promise<CloudBatchListResponse> => {
  const response = await axios.get(
    `${nuclearn_api_url}/datasets/${datasetId}/upload-from-cloud`,
  );
  return response.data;
};

/**
 * Poll batch status.
 * GET /datasets/{id}/upload-from-cloud/{batch_id}
 */
const getBatchStatus = async (
  datasetId: number,
  batchId: string,
  includeItems = false,
): Promise<CloudBatchStatusResponse> => {
  const response = await axios.get(
    `${nuclearn_api_url}/datasets/${datasetId}/upload-from-cloud/${batchId}`,
    { params: { include_items: includeItems } },
  );
  return response.data;
};

/**
 * Cancel a batch upload.
 * DELETE /datasets/{id}/upload-from-cloud/{batch_id}
 */
const cancelBatchUpload = async (
  datasetId: number,
  batchId: string,
): Promise<void> => {
  await axios.delete(
    `${nuclearn_api_url}/datasets/${datasetId}/upload-from-cloud/${batchId}`,
  );
};

// =============================================================================
// Batch Upload React Query Hooks
// =============================================================================

/**
 * Hook to start a batch upload from cloud storage.
 */
export const useStartBatchUpload = (datasetId: number) => {
  return useMutation(
    (request: CloudBatchUploadRequest) => startBatchUpload(datasetId, request),
    {
      // Rate limit handling: retry with exponential backoff
      retry: (failureCount, error) => {
        return isRateLimitError(error) && failureCount < MAX_RATE_LIMIT_RETRIES;
      },
      retryDelay: (attemptIndex, error) => {
        const baseDelay = getRetryAfterDelay(error);
        return baseDelay * Math.pow(2, attemptIndex);
      },
    },
  );
};

/**
 * Check if a batch status indicates a terminal (non-progressing) state.
 */
export const isCloudBatchTerminal = (status: CloudBatchStatus): boolean => {
  return TERMINAL_STATUSES.includes(status);
};

/** @deprecated Use isCloudBatchTerminal instead */
export const isCloudBatchSummaryComplete = isCloudBatchTerminal;

/**
 * Partition a flat list of cloud batches into live-polling active
 * batches and already-finished recent batches, filtering out any
 * batch_ids the user has dismissed from the UI.
 */
export const partitionBatches = (
  batches: CloudBatchSummary[],
  dismissed: ReadonlySet<string>,
): { active: CloudBatchSummary[]; recent: CloudBatchSummary[] } => {
  const active: CloudBatchSummary[] = [];
  const recent: CloudBatchSummary[] = [];
  for (const batch of batches) {
    if (dismissed.has(batch.batch_id)) continue;
    if (isCloudBatchTerminal(batch.status)) {
      recent.push(batch);
    } else {
      active.push(batch);
    }
  }
  return { active, recent };
};

/**
 * Decide the next refetch interval for the active-batches query.
 *
 * - Empty response → stop polling.
 * - Active batches + SSE healthy → stop polling; SSE drives invalidation.
 * - Active batches + SSE unhealthy → poll as fallback.
 * - Recent-only (terminal) batches → keep polling so the Recent Imports
 *   section watches batches age out of the backend's grace window; SSE
 *   doesn't fire an event for aging-out, so we need polling regardless of
 *   stream health.
 */
export const getBatchPollInterval = (
  data: CloudBatchListResponse | undefined,
): number | false => {
  if (!data?.batches?.length) return false;
  const hasActiveBatch = data.batches.some(
    (batch) => !isCloudBatchTerminal(batch.status),
  );
  if (hasActiveBatch && isNotificationStreamHealthy()) return false;
  return BATCH_POLL_INTERVAL_MS;
};

/**
 * Hook to list active and recently-completed batches for a dataset.
 * SSE drives cache invalidation for active batches; polling falls back when
 * SSE is unhealthy and also keeps Recent Imports in sync as terminal batches
 * age out of the backend's grace window.
 */
export const useActiveBatches = (datasetId: number, enabled = true) => {
  const queryClient = useQueryClient();

  const query = useQuery(
    ["cloud-batches", datasetId],
    () => listActiveBatches(datasetId),
    {
      enabled: enabled && !!datasetId,
      refetchInterval: getBatchPollInterval,
      staleTime: 1000,
    },
  );

  // Subscribe to every active batch's stream, not just the first one — a
  // dataset can have multiple concurrent uploads, and the list query needs to
  // refetch when ANY of them changes state. Subscribing only to the first
  // leaves the rest appearing frozen until the polling fallback kicks in.
  const activeBatchStreamKeys =
    query.data?.batches
      ?.filter((b) => !isCloudBatchTerminal(b.status))
      .map((b) => `notify:batch:${b.batch_id}:status`) ?? [];

  useNotificationStreams(activeBatchStreamKeys, "batch_status", () => {
    queryClient.invalidateQueries(["cloud-batches", datasetId]);
  });

  useSSEFallbackInvalidation(
    queryClient,
    ["cloud-batches", datasetId],
    !!datasetId,
  );

  return query;
};

/**
 * Hook to get a specific batch status — SSE for real-time, polling as fallback.
 */
export const useBatchStatus = (
  datasetId: number,
  batchId: string | null,
  options?: {
    includeItems?: boolean;
    refetchInterval?: number;
    enabled?: boolean;
  },
) => {
  const {
    includeItems = false,
    refetchInterval = 2000,
    enabled = true,
  } = options ?? {};
  const queryClient = useQueryClient();
  const queryKey = ["cloud-batch-status", datasetId, batchId, includeItems];

  const query = useQuery(
    queryKey,
    () => getBatchStatus(datasetId, batchId!, includeItems),
    {
      enabled: enabled && !!datasetId && !!batchId,
      refetchInterval: (data) => {
        if (data && isCloudBatchTerminal(data.status)) {
          return false;
        }
        if (isNotificationStreamHealthy() && batchId) {
          return false;
        }
        return refetchInterval;
      },
      staleTime: 1000,
    },
  );

  // Subscribe to SSE updates for this batch
  const streamKey = batchId ? `notify:batch:${batchId}:status` : null;

  useNotificationStream(streamKey, "batch_status", (raw) => {
    const data = raw as unknown as BatchStatusEvent;
    queryClient.setQueryData<CloudBatchStatusResponse | undefined>(
      queryKey,
      (prev) => {
        if (!prev) return prev;
        // Same-reference skip — avoid re-rendering every subscriber when
        // an SSE event repeats state already in the cache.
        if (
          (data.status === undefined || data.status === prev.status) &&
          (data.files_discovered === undefined ||
            data.files_discovered === prev.discovery.files_found) &&
          (data.files_completed === undefined ||
            data.files_completed === prev.uploads.completed) &&
          (data.files_failed === undefined ||
            data.files_failed === prev.uploads.failed) &&
          (data.files_uploading === undefined ||
            data.files_uploading === prev.uploads.uploading) &&
          (data.files_pending === undefined ||
            data.files_pending === prev.uploads.pending)
        ) {
          return prev;
        }
        // The cache shape requires non-null numeric counters, but the
        // SSE payload types all those fields as number | null. Treat a
        // null event value as "leave unchanged" so we never write a
        // null into fields the cache declares non-null.
        return {
          ...prev,
          status:
            data.status !== undefined
              ? (data.status as CloudBatchStatus)
              : prev.status,
          discovery: {
            ...prev.discovery,
            files_found: data.files_discovered ?? prev.discovery.files_found,
          },
          uploads: {
            ...prev.uploads,
            completed: data.files_completed ?? prev.uploads.completed,
            failed: data.files_failed ?? prev.uploads.failed,
            uploading: data.files_uploading ?? prev.uploads.uploading,
            pending: data.files_pending ?? prev.uploads.pending,
          },
        };
      },
    );
  });

  useSSEFallbackInvalidation(queryClient, queryKey, !!batchId);

  return query;
};

/**
 * Hook to cancel a batch upload.
 */
export const useCancelBatchUpload = (datasetId: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    (batchId: string) => cancelBatchUpload(datasetId, batchId),
    {
      onSuccess: () => {
        // Invalidate active batches to refresh the list
        queryClient.invalidateQueries(["cloud-batches", datasetId]);
      },
    },
  );
};

/** @deprecated Use isCloudBatchTerminal instead */
export const isCloudBatchComplete = isCloudBatchTerminal;

/**
 * Calculate progress percentage for a batch.
 */
export const calculateCloudBatchProgress = (
  batch: CloudBatchStatusResponse,
): number => {
  // Defensive checks for missing data
  if (!batch?.uploads) return 0;

  const uploads = batch.uploads;
  const total =
    (uploads.pending ?? 0) +
    (uploads.uploading ?? 0) +
    (uploads.completed ?? 0) +
    (uploads.failed ?? 0);
  if (total === 0) return 0;
  return Math.round(
    (((uploads.completed ?? 0) + (uploads.failed ?? 0)) / total) * 100,
  );
};

/**
 * Helper to create a batch upload request from CloudFileSelectContent selections.
 */
export const createBatchUploadRequest = (
  providerType: CloudProviderType,
  connectionId: number,
  selectedFiles: Array<{ path: string; is_directory: boolean }>,
  folderOptions?: Map<
    string,
    {
      maxFolderDepth: number;
      filterByExtension: boolean;
      selectedExtensions: string[];
    }
  >,
  globalOptions?: {
    maxFolderDepth?: number;
    fileExtensions?: string[];
    taskPriority?: number;
    triggerDocWorkflow?: boolean;
  },
): CloudBatchUploadRequest => {
  const paths: CloudPathItem[] = selectedFiles.map((file) => {
    const isFolder = file.is_directory;
    const folderOpts = isFolder ? folderOptions?.get(file.path) : undefined;

    return {
      path: file.path,
      is_folder: isFolder,
      // Per-folder options override global options
      max_folder_depth:
        folderOpts?.maxFolderDepth ?? globalOptions?.maxFolderDepth ?? 1,
      file_extensions:
        folderOpts?.filterByExtension &&
        folderOpts.selectedExtensions.length > 0
          ? folderOpts.selectedExtensions
          : globalOptions?.fileExtensions,
    };
  });

  return {
    provider_type: providerType,
    external_connection_id: connectionId,
    paths,
    task_priority: globalOptions?.taskPriority ?? 4,
    trigger_doc_workflow: globalOptions?.triggerDocWorkflow ?? true,
  };
};
